'use client';
import React, { useState } from 'react';
import AppLayout from '@/components/AppLayout';
import Icon from '@/components/ui/AppIcon';

interface Review {
  id: string;
  dealId: string;
  brand: string;
  date: string;
  scores: { label: string; value: number }[];
  overall: number;
  status: 'verified' | 'under_review' | 'contested';
  comment?: string;
}

const reviews: Review[] = [
  {
    id: 'rev-001', dealId: 'DZ-2026-0001842', brand: 'Djezzy', date: '12 Aoû 2026',
    scores: [
      { label: 'Ponctualité', value: 5 }, { label: 'Respect du brief', value: 5 },
      { label: 'Qualité d\'exécution', value: 4 }, { label: 'Communication', value: 5 },
      { label: 'Fiabilité', value: 5 },
    ],
    overall: 4.8, status: 'verified', comment: 'Collaboration exemplaire, livraison avant délai.'
  },
  {
    id: 'rev-002', dealId: 'DZ-2026-0001756', brand: 'Ooredoo Algérie', date: '28 Juil 2026',
    scores: [
      { label: 'Ponctualité', value: 4 }, { label: 'Respect du brief', value: 5 },
      { label: 'Qualité d\'exécution', value: 5 }, { label: 'Communication', value: 4 },
      { label: 'Fiabilité', value: 5 },
    ],
    overall: 4.6, status: 'verified'
  },
  {
    id: 'rev-003', dealId: 'DZ-2026-0001690', brand: 'Cevital', date: '10 Juil 2026',
    scores: [
      { label: 'Ponctualité', value: 3 }, { label: 'Respect du brief', value: 4 },
      { label: 'Qualité d\'exécution', value: 4 }, { label: 'Communication', value: 3 },
      { label: 'Fiabilité', value: 4 },
    ],
    overall: 3.6, status: 'under_review', comment: 'Délai légèrement dépassé.'
  },
];

function StarRating({ value, max = 5 }: { value: number; max?: number }) {
  return (
    <div className="flex items-center gap-0.5">
      {Array.from({ length: max }).map((_, i) => (
        <Icon key={i} name="StarIcon" size={12} className={i < Math.round(value) ? 'text-amber-400' : 'text-muted'} />
      ))}
    </div>
  );
}

function StatusPill({ status }: { status: Review['status'] }) {
  const map = {
    verified: { label: 'Vérifié', cls: 'bg-positive/10 text-positive border-positive/20' },
    under_review: { label: 'En révision', cls: 'bg-warning/10 text-warning border-warning/20' },
    contested: { label: 'Contesté', cls: 'bg-negative/10 text-negative border-negative/20' },
  };
  const s = map[status];
  return <span className={`text-[10px] font-700 uppercase tracking-wide px-2 py-0.5 rounded-full border ${s.cls}`}>{s.label}</span>;
}

export default function ProfessionalReputationPage() {
  const [contestModal, setContestModal] = useState<Review | null>(null);
  const [contestReason, setContestReason] = useState('');
  const [contestEvidence, setContestEvidence] = useState('');
  const [submitted, setSubmitted] = useState(false);

  const prsScore = 82;

  const handleContest = () => {
    setSubmitted(true);
    setTimeout(() => { setContestModal(null); setSubmitted(false); setContestReason(''); setContestEvidence(''); }, 2000);
  };

  return (
    <AppLayout currentPath="/professional-reputation">
      <div className="space-y-8 animate-fade-in">
        {/* Header */}
        <div>
          <div className="flex items-center gap-2 mb-1">
            <span className="text-xs font-700 uppercase tracking-widest text-teal-400 bg-teal-400/10 px-2 py-0.5 rounded-full">PRS v1.0</span>
          </div>
          <h1 className="text-2xl font-700 text-foreground">Professional Reputation Score</h1>
          <p className="text-sm text-muted-foreground mt-1">Évaluations post-collaboration vérifiées · Séparé des performances d'audience</p>
        </div>

        {/* PRS Overview */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <div className="sm:col-span-1 bg-card border border-border rounded-xl p-6 flex flex-col items-center justify-center gap-2">
            <div className="w-20 h-20 rounded-full border-4 border-primary/30 flex items-center justify-center bg-primary/5">
              <span className="text-3xl font-700 font-mono text-primary">{prsScore}</span>
            </div>
            <p className="text-sm font-600 text-foreground">PRS Global</p>
            <p className="text-xs text-muted-foreground">Sur 100 · Basé sur {reviews.length} évaluations vérifiées</p>
            <div className="flex items-center gap-1 mt-1">
              <StarRating value={4.6} />
              <span className="text-xs text-muted-foreground ml-1">4.6/5</span>
            </div>
          </div>
          <div className="sm:col-span-2 bg-card border border-border rounded-xl p-6">
            <h3 className="text-sm font-700 text-foreground mb-4">Critères détaillés</h3>
            <div className="space-y-3">
              {[
                { label: 'Ponctualité', value: 88 }, { label: 'Respect du brief', value: 94 },
                { label: 'Qualité d\'exécution', value: 90 }, { label: 'Communication', value: 85 },
                { label: 'Fiabilité', value: 96 },
              ].map(c => (
                <div key={c.label} className="flex items-center gap-3">
                  <span className="text-xs text-muted-foreground w-36 flex-shrink-0">{c.label}</span>
                  <div className="flex-1 bg-muted rounded-full h-1.5">
                    <div className="h-1.5 rounded-full bg-primary transition-all" style={{ width: `${c.value}%` }} />
                  </div>
                  <span className="text-xs font-mono font-600 text-foreground w-8 text-right">{c.value}%</span>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Anti-fake notice */}
        <div className="bg-info/5 border border-info/20 rounded-xl p-4 flex gap-3">
          <Icon name="ShieldCheckIcon" size={18} className="text-info flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-sm font-600 text-foreground">Protections anti-faux avis ISX</p>
            <p className="text-xs text-muted-foreground mt-1">Seules les évaluations liées à un ISX Deal ID vérifié sont acceptées. Les conflits d'intérêts sont détectés automatiquement. Les campagnes organisées de mauvaises notes sont bloquées. Chaque évaluation est modérée avant publication. Le créateur est notifié et peut contester gratuitement.</p>
          </div>
        </div>

        {/* Reviews */}
        <div>
          <h2 className="text-base font-700 text-foreground mb-4">Évaluations vérifiées ({reviews.length})</h2>
          <div className="space-y-4">
            {reviews.map(review => (
              <div key={review.id} className="bg-card border border-border rounded-xl p-5">
                <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-3 mb-4">
                  <div>
                    <div className="flex items-center gap-2 mb-1">
                      <span className="text-sm font-700 text-foreground">{review.brand}</span>
                      <StatusPill status={review.status} />
                    </div>
                    <div className="flex items-center gap-3 text-xs text-muted-foreground">
                      <span>Deal ID: <span className="font-mono text-foreground">{review.dealId}</span></span>
                      <span>·</span>
                      <span>{review.date}</span>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="flex items-center gap-1.5">
                      <StarRating value={review.overall} />
                      <span className="text-sm font-700 font-mono text-foreground">{review.overall}/5</span>
                    </div>
                    {review.status === 'verified' && (
                      <button onClick={() => setContestModal(review)} className="text-xs text-muted-foreground hover:text-negative transition-colors flex items-center gap-1">
                        <Icon name="FlagIcon" size={12} />
                        Contester
                      </button>
                    )}
                  </div>
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-5 gap-2 mb-3">
                  {review.scores.map(s => (
                    <div key={s.label} className="bg-muted/40 rounded-lg p-2 text-center">
                      <p className="text-[10px] text-muted-foreground mb-1">{s.label}</p>
                      <StarRating value={s.value} />
                    </div>
                  ))}
                </div>
                {review.comment && (
                  <p className="text-xs text-muted-foreground italic border-l-2 border-border pl-3">"{review.comment}"</p>
                )}
                {review.status === 'under_review' && (
                  <div className="mt-3 bg-warning/5 border border-warning/20 rounded-lg px-3 py-2 flex items-center gap-2">
                    <Icon name="ClockIcon" size={14} className="text-warning" />
                    <p className="text-xs text-warning">Cette évaluation est en cours de révision humaine. Délai estimé : 5–7 jours ouvrés.</p>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Contest Modal */}
        {contestModal && (
          <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
            <div className="bg-card border border-border rounded-2xl p-6 w-full max-w-md shadow-2xl">
              {submitted ? (
                <div className="flex flex-col items-center gap-3 py-4">
                  <div className="w-12 h-12 rounded-full bg-positive/10 flex items-center justify-center">
                    <Icon name="CheckCircleIcon" size={24} className="text-positive" />
                  </div>
                  <p className="text-base font-700 text-foreground">Contestation enregistrée</p>
                  <p className="text-sm text-muted-foreground text-center">Dossier #ISX-DISP-2026-{Math.floor(Math.random() * 9000 + 1000)} créé. Délai estimé : 5–7 jours ouvrés.</p>
                </div>
              ) : (
                <>
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-base font-700 text-foreground">Contester l'évaluation</h3>
                    <button onClick={() => setContestModal(null)} className="text-muted-foreground hover:text-foreground">
                      <Icon name="XMarkIcon" size={18} />
                    </button>
                  </div>
                  <p className="text-xs text-muted-foreground mb-4">Deal ID: <span className="font-mono text-foreground">{contestModal.dealId}</span> · {contestModal.brand}</p>
                  <div className="space-y-4">
                    <div>
                      <label className="block text-xs font-600 text-muted-foreground uppercase tracking-wide mb-2">Raison de la contestation</label>
                      <select value={contestReason} onChange={e => setContestReason(e.target.value)} className="w-full bg-muted border border-border rounded-lg px-3 py-2 text-sm text-foreground outline-none focus:border-primary">
                        <option value="">Sélectionner une raison</option>
                        <option value="incorrect">Données incorrectes</option>
                        <option value="methodology">Méthodologie inadaptée</option>
                        <option value="fake">Évaluation frauduleuse</option>
                        <option value="conflict">Conflit d'intérêts</option>
                        <option value="other">Autre</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-xs font-600 text-muted-foreground uppercase tracking-wide mb-2">Preuves / Explication</label>
                      <textarea value={contestEvidence} onChange={e => setContestEvidence(e.target.value)} rows={3} placeholder="Décrivez les éléments qui justifient cette contestation..." className="w-full bg-muted border border-border rounded-lg px-3 py-2 text-sm text-foreground outline-none focus:border-primary resize-none" />
                    </div>
                    <p className="text-[11px] text-muted-foreground">La contestation est gratuite. Un numéro de dossier vous sera attribué immédiatement. La décision finale peut faire l'objet d'une révision humaine.</p>
                    <div className="flex gap-2">
                      <button onClick={() => setContestModal(null)} className="flex-1 px-4 py-2 bg-muted border border-border rounded-lg text-sm font-600 text-foreground hover:bg-muted/80 transition-colors">Annuler</button>
                      <button onClick={handleContest} disabled={!contestReason} className="flex-1 px-4 py-2 bg-primary hover:bg-primary/90 text-primary-foreground rounded-lg text-sm font-600 transition-colors disabled:opacity-50">Soumettre</button>
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>
        )}
      </div>
    </AppLayout>
  );
}
